/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useCurrentUser } from "actions/CurrentUserActions";
import { useMemo } from "react";

export interface DescribedEnhanceSpeechLimits {
  fileSizeLimitBytes: number;
  fileSizeLimitDescription: string;
  fileDurationLimitSeconds: number;
  fileDurationLimitDescription: string;
  dailyDurationLimitInHours: number;
  dailyDurationLimitDescription: string;
  pollingTimeoutSeconds: number;
  comprehensiveLimitsDescription: string;
}

export const useEnhanceSpeechLimits = (): DescribedEnhanceSpeechLimits => {
  const { currentUser } = useCurrentUser();
  const limits = currentUser?.enhanceSpeechLimits;
  const pollingTimeoutSeconds = limits?.pollingTimeoutSeconds ?? 600;

  const fileSizeLimitBytes = limits?.fileSizeLimitBytes ?? 524_288_000;
  const fileSizeLimitDescription = I18n.numberToHumanSize(fileSizeLimitBytes);

  const fileDurationLimitSeconds = limits?.fileDurationLimitSeconds ?? 3600;
  const fileDurationLimitInHours = Math.round(
    fileDurationLimitSeconds / 60 / 60
  );
  const fileDurationLimitInMinutes = Math.round(fileDurationLimitSeconds / 60);

  let fileDurationLimitDescription: string;
  if (fileDurationLimitInMinutes < 60) {
    fileDurationLimitDescription = I18n.t("enhance_speech.minutes", {
      minutes: fileDurationLimitInMinutes,
    });
  } else {
    fileDurationLimitDescription = I18n.t("enhance_speech.hours", {
      count: fileDurationLimitInHours,
    });
  }

  const dailyDurationLimitInHours = Math.round(
    (limits?.dailyDurationLimitSeconds ?? 0) / 60 / 60
  );
  const dailyDurationLimitDescription = I18n.t("enhance_speech.hours", {
    count: dailyDurationLimitInHours,
  });

  const comprehensiveLimitsDescription = I18n.t("enhance_speech.limits", {
    fileSizeLimitDescription,
    fileDurationLimitDescription,
    dailyDurationLimitDescription,
  });

  return useMemo(
    () => ({
      fileSizeLimitBytes,
      fileSizeLimitDescription,
      fileDurationLimitSeconds,
      fileDurationLimitDescription,
      dailyDurationLimitInHours,
      dailyDurationLimitDescription,
      pollingTimeoutSeconds,
      comprehensiveLimitsDescription,
    }),
    [
      comprehensiveLimitsDescription,
      dailyDurationLimitDescription,
      dailyDurationLimitInHours,
      fileDurationLimitDescription,
      fileDurationLimitSeconds,
      fileSizeLimitBytes,
      fileSizeLimitDescription,
      pollingTimeoutSeconds,
    ]
  );
};
